import {
  type AllowedMimeType,
  AllowedMimeType as AllowedMimeTypeSchema,
  MAX_FILE_BYTES,
  mimeTypeForFilename,
  type MyFileItem,
  type PresignedUpload,
  type SuggestionSaved,
  type TodoAttachment,
} from '@apm/contracts';
import { apiFetch } from './http';
import { sha256Hex as sha256OfBytes } from './sha256';

/**
 * Tải một tệp lên S3 — luồng ba bước của `APM-202`.
 *
 * ```
 * POST /files/presign   → app kiểm quyền, trả presigned PUT
 * PUT  <presigned url>  → trình duyệt gửi THẲNG S3, không qua Node (luật #3)
 * POST /files/:id/commit → app HEAD object, đổi trạng thái sang `committed`
 * ```
 *
 * Nằm ở `lib/` chứ không ở một feature: đính kèm task, phiên bản tài liệu và
 * ảnh đại diện đều đi đúng ba bước này. Mỗi màn hình tự viết lại là mỗi màn
 * hình có thể quên bước `commit` — và một file `pending` thì tải về ra 412.
 */
export async function uploadFile(
  file: File,
  /**
   * `purpose: 'document'` — the first version of a **new** project document
   * (no `documentId` yet): the API asks `document:write` instead of
   * `task:create` (APM-PSPACE-i).
   */
  target: { projectId: string; documentId?: string; purpose?: 'bug_evidence' | 'document' },
): Promise<string> {
  const presigned = await apiFetch<PresignedUpload>('/files/presign', {
    method: 'POST',
    body: {
      projectId: target.projectId,
      ...(target.documentId ? { documentId: target.documentId } : {}),
      // Bằng chứng lỗi — trần riêng: ảnh 5 MB, video 100 MB (APM-BUGF-a).
      ...(target.purpose ? { purpose: target.purpose } : {}),
      filename: file.name,
      mimeType: resolveMimeType(file),
      sizeBytes: file.size,
      checksumSha256: await sha256Hex(file),
    },
  });

  const response = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    // Header bắt buộc nằm trong chữ ký; thiếu một cái là S3 từ chối. Đó là
    // chủ đích — client không đổi được content type sau khi app đã duyệt.
    headers: presigned.requiredHeaders,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Tải tệp lên kho lưu trữ thất bại (HTTP ${response.status})`);
  }

  await apiFetch(`/files/${presigned.fileId}/commit`, { method: 'POST' });
  return presigned.fileId;
}

/**
 * Tải một file đính kèm của to-do cá nhân — APM-TODO-d, docs/plan-todo.md §2.8.
 *
 * Cùng ba bước với `uploadFile` (presign → PUT thẳng S3 → commit, luật #3),
 * khác đường dẫn: file to-do không thuộc dự án nào, nên không có `projectId`.
 * Trả về file đã commit để hộp thoại hiện ngay dưới editor.
 */
export async function uploadTodoFile(file: File): Promise<TodoAttachment> {
  const presigned = await apiFetch<PresignedUpload>('/me/todo-files/presign', {
    method: 'POST',
    body: {
      filename: file.name,
      mimeType: resolveMimeType(file),
      sizeBytes: file.size,
      checksumSha256: await sha256Hex(file),
    },
  });

  const response = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: presigned.requiredHeaders,
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Tải tệp lên kho lưu trữ thất bại (HTTP ${response.status})`);
  }

  return apiFetch<TodoAttachment>(`/me/todo-files/${presigned.fileId}/commit`, { method: 'POST' });
}

/**
 * Tải một tệp cá nhân lên trang Tệp — APM-MYF-c, docs/plan-my-files.md §2.3.
 *
 * Cùng ba bước (presign → PUT thẳng S3 → commit, luật #3), khác hai chỗ:
 *
 * - **Mọi định dạng.** Không qua `resolveMimeType` (danh sách cho phép của file
 *   dự án): gửi đúng giá trị trình duyệt nói, rỗng thì để server suy từ đuôi
 *   (`personalFileMimeType`). Một `.py` hay `.exe` là hợp lệ ở đây.
 * - **Có tiến độ.** `fetch` không báo tiến độ tải **lên**, nên bước PUT đi bằng
 *   `XMLHttpRequest` — trang Tệp vẽ một thanh cho từng file.
 */
export async function uploadMyFile(
  file: File,
  onProgress?: (fraction: number) => void,
  /** Tải thẳng vào một thư mục tự tạo — APM-MYF-j, plan §5.3. */
  folderId?: string,
  /** Tài liệu lưu từ bản nháp — nháp bị xoá cùng lúc commit (APM-MYF-l, plan §5.4). */
  fromDraftId?: string,
): Promise<MyFileItem> {
  const fileId = await presignAndPutMyFile(file, onProgress, folderId);
  return apiFetch<MyFileItem>(`/me/files/${fileId}/commit`, {
    method: 'POST',
    ...(fromDraftId ? { body: { fromDraftId } } : {}),
  });
}

/**
 * Lưu bản mới của một tài liệu `.md` cá nhân — APM-MYF-f, plan §2.4.
 *
 * Bản mới đi **cùng** hai bước đầu của `uploadMyFile` (presign → PUT thẳng S3)
 * nhưng **không** commit: `POST /me/files/:id/revise` đổi chỗ trong một
 * transaction — bản mới `committed`, bản cũ `deleted` — nên danh sách chỉ còn
 * một bản. Hỏng giữa chừng chỉ để lại một hàng `pending` mà job dọn đã biết xử lý.
 */
export async function reviseMyDocument(previousId: string, file: File): Promise<MyFileItem> {
  const pendingFileId = await presignAndPutMyFile(file);
  return apiFetch<MyFileItem>(`/me/files/${previousId}/revise`, {
    method: 'POST',
    body: { pendingFileId },
  });
}

/**
 * "Lưu thành phiên bản khác" — APM-MYF-n. Như `reviseMyDocument` (presign → PUT thẳng S3,
 * không commit), nhưng `POST /me/files/:id/versions` **giữ** bản cũ làm phiên bản trước.
 */
export async function saveMyDocumentVersion(
  previousId: string,
  file: File,
  note?: string,
): Promise<MyFileItem> {
  const pendingFileId = await presignAndPutMyFile(file);
  return apiFetch<MyFileItem>(`/me/files/${previousId}/versions`, {
    method: 'POST',
    body: note ? { pendingFileId, note } : { pendingFileId },
  });
}

/**
 * Lưu một lượt **đề xuất** trên tài liệu người khác chia sẻ — APM-CD2-i. Ba bước như mọi lượt
 * tải lên, nhưng qua cửa của lượt cấp quyền: bản lưu thành phiên bản mới của tài liệu **chủ**.
 * `baseFileId` là bản đang soạn — chủ đã lưu bản khác thì server trả 409.
 */
export async function saveSuggestion(
  grantId: string,
  baseFileId: string,
  file: File,
): Promise<SuggestionSaved> {
  if (file.size === 0) throw new Error(`"${file.name}" là tệp rỗng — không có gì để tải lên.`);
  const base = `/me/shared/${grantId}`;
  const presigned = await apiFetch<PresignedUpload>(`${base}/suggestion-upload`, {
    method: 'POST',
    body: { baseFileId, sizeBytes: file.size, checksumSha256: await sha256Hex(file) },
  });
  await putWithProgress(presigned, file);
  return apiFetch<SuggestionSaved>(`${base}/suggestion-commit`, {
    method: 'POST',
    body: { baseFileId, pendingFileId: presigned.fileId },
  });
}

async function presignAndPutMyFile(
  file: File,
  onProgress?: (fraction: number) => void,
  folderId?: string,
): Promise<string> {
  if (file.size === 0) throw new Error(`"${file.name}" là tệp rỗng — không có gì để tải lên.`);
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(`"${file.name}" vượt quá ${Math.round(MAX_FILE_BYTES / 1024 ** 2)} MB.`);
  }

  const presigned = await apiFetch<PresignedUpload>('/me/files/presign', {
    method: 'POST',
    body: {
      filename: file.name,
      // Bỏ tham số (`;charset=…`): server chỉ nhận đúng dạng `type/subtype`.
      mimeType: file.type.split(';')[0]!.trim(),
      sizeBytes: file.size,
      checksumSha256: await sha256Hex(file),
      ...(folderId ? { folderId } : {}),
    },
  });

  await putWithProgress(presigned, file, onProgress);
  return presigned.fileId;
}

function putWithProgress(
  presigned: PresignedUpload,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presigned.uploadUrl);
    for (const [name, value] of Object.entries(presigned.requiredHeaders)) {
      // Header cấm của trình duyệt — nó tự đặt từ body; gọi `setRequestHeader`
      // chỉ để lại một dòng lỗi "Refused to set unsafe header" trên console.
      if (name.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(new Error(`Tải tệp lên kho lưu trữ thất bại (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Tải tệp lên kho lưu trữ thất bại (mất kết nối)'));
    xhr.send(file);
  });
}

/**
 * MIME sẽ khai với server — APM-DOC-a.
 *
 * Ba nhánh, và nhánh nào cũng phải kết thúc bằng một câu người dùng hiểu:
 *
 * 1. Trình duyệt nói một giá trị hợp lệ ⇒ tin nó. Hệ điều hành biết rõ hơn một
 *    bảng tra viết tay.
 * 2. Trình duyệt **im lặng** (`.md` trên phần lớn máy) ⇒ suy từ đuôi tệp.
 * 3. Trình duyệt nói một giá trị **không** được phép ⇒ hỏng **ở đây**, với tên
 *    loại tệp thật. Trước đây nhánh này đi tiếp tới server rồi quay về "Dữ liệu
 *    chưa hợp lệ" — một câu đúng về mặt kỹ thuật và vô dụng với người đang cầm
 *    một file trong tay.
 *
 * ⚠️ Nhánh 2 **không** được lấn sang nhánh 3. Giá trị này đi vào
 * `requiredHeaders` của presigned PUT nên nó nằm trong chữ ký và thành content
 * type S3 ghi nhận vĩnh viễn; để đuôi tệp phủ quyết thông tin thật của hệ điều
 * hành là để một tệp đổi tên tự khai mình là thứ khác.
 */
function resolveMimeType(file: File): AllowedMimeType {
  const declared = file.type.trim();

  if (declared === '') {
    const guessed = mimeTypeForFilename(file.name);
    if (guessed) return guessed;
    throw new Error(
      `Không nhận ra loại tệp của "${file.name}". Hãy đổi tên tệp cho đúng đuôi (ví dụ .md, .docx, .xlsx) rồi thử lại.`,
    );
  }

  const parsed = AllowedMimeTypeSchema.safeParse(declared);
  if (parsed.success) return parsed.data;

  throw new Error(`Hệ thống chưa nhận tệp loại "${declared}".`);
}

/**
 * SHA-256 của tệp, hex thường.
 *
 * Tính ở client vì server không bao giờ nhìn thấy nội dung tệp — nó chỉ so
 * kích thước lúc commit. Checksum là thứ duy nhất phát hiện được tệp hỏng
 * đường truyền mà vẫn đủ byte.
 */
async function sha256Hex(file: File): Promise<string> {
  // Không gọi thẳng `crypto.subtle`: nó không có khi mở app bằng IP LAN — `lib/sha256.ts`.
  return sha256OfBytes(await file.arrayBuffer());
}
